import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  RefreshCw,
  Eraser,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import {
  PasswordChecklist,
  isPasswordValid,
} from "../components/PasswordChecklist";
import { PasswordField } from "../components/PasswordField";
import EmployerLegalFooter from "../components/EmployerLegalFooter";
import { readSessionNotice } from "../lib/sessionSync";
import { AuthHeroPanel } from "../components/AuthHeroPanel";

const EMBEDDED = (() => { try { return window.self !== window.top; } catch { return true; } })();

const E1_LOGO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_err-dashboard-portal/artifacts/phsk29o5_E1-Primary-Logo_Small_Transparent.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const [stage, setStage] = useState("credentials"); // credentials | code | new-password
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changeToken, setChangeToken] = useState("");
  const [code, setCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sso, setSso] = useState({ enabled: false });
  const [sessionNotice, setSessionNotice] = useState(readSessionNotice);

  useEffect(() => {
    api
      .get("/auth/sso/status")
      .then(({ data }) => setSso(data))
      .catch(() => {});
    const ssoError = new URLSearchParams(window.location.search).get(
      "sso_error",
    );
    if (ssoError) setError(decodeURIComponent(ssoError.replace(/\+/g, " ")));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendCode = async () => {
    setError("");
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-2fa", {
        verification_token: verificationToken,
      });
      setVerificationToken(data.verification_token);
      setEmailHint(data.email_hint);
      setCode("");
      setCooldown(30);
      toast.success("A new code is on its way to your work email");
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) ||
          "Could not send a new code. Please sign in again.",
      );
    } finally {
      setResending(false);
    }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setError("");
    setSessionNotice("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.trusted_browser) {
        completeLogin(data.access_token, data.user);
        toast.success(`Welcome back, ${data.user.name}`);
        navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
        return;
      }
      setVerificationToken(data.verification_token);
      setEmailHint(data.email_hint);
      setStage("code");
      toast.success("Verification code sent");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", {
        verification_token: verificationToken,
        code,
      });
      if (data.password_change_required) {
        setChangeToken(data.change_token);
        setStage("new-password");
        setCode("");
        toast.info(
          "Your password must be updated to meet the current security policy",
        );
        return;
      }
      completeLogin(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Both passwords must match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forced-password-change", {
        change_token: changeToken,
        new_password: newPassword,
      });
      toast.success("Password updated. Sign in with your new password.");
      setStage("credentials");
      setNewPassword("");
      setConfirmPassword("");
      setChangeToken("");
      setPassword("");
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) ||
          "Could not update your password",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <div className="grid flex-1 grid-cols-1 lg:grid-cols-5">
        {/* Hero */}
        <AuthHeroPanel testId="login-hero-panel" logoTestId="employeesfirst-login-logo"
          eyebrow="Client Access · Encrypted"
          footer={<><ShieldCheck size={14} /> Multi-factor · Email OTP · Session bound</>}>
          <h1 className="font-heading font-light text-5xl xl:text-6xl leading-none tracking-tight mb-6">
            Your captive.
            <br />
            <span className="font-black">Clearly measured.</span>
          </h1>
          <p className="text-[15px] leading-relaxed opacity-90 max-w-md">
            Track Experience Rated Refunds (ERRs), submit vendor invoices,
            and understand the medical condition prevalence of your covered
            population — all in one authenticated workspace.
          </p>
        </AuthHeroPanel>

        {/* Form */}
        <div className="col-span-1 lg:col-span-3 flex items-center justify-center px-6 py-12 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <img
              src={E1_LOGO}
              alt="EmployeesFirst"
              className="mb-10 h-12 max-w-[260px] w-auto object-contain lg:hidden"
              width="260"
              height="32"
              data-testid="employeesfirst-mobile-login-logo"
            />
            <div className="eyebrow mb-3">
              {stage === "credentials"
                ? "Sign In"
                : stage === "new-password"
                  ? "Update your password"
                  : "Two-factor authentication"}
            </div>
            <h2
              className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-2"
              style={{ color: "var(--ink)" }}
            >
              {stage === "credentials"
                ? "Access your program financials"
                : stage === "new-password"
                  ? "Set a stronger password."
                  : "Confirm it's you."}
            </h2>
            <p className="text-sm mt-3 mb-10" style={{ color: "var(--muted)" }}>
              {stage === "credentials" ? (
                "Enter your credentials. A one-time code will be sent to your registered work email."
              ) : stage === "new-password" ? (
                "Your password predates our current security policy. Choose a new one of at least 12 characters with an uppercase letter, a lowercase letter, a number and a symbol. Passwords found in known breaches are not accepted."
              ) : (
                <>
                  Enter the 6-digit code we sent to{" "}
                  <span
                    className="font-semibold"
                    style={{ color: "var(--ink)" }}
                  >
                    {emailHint}
                  </span>
                  . Codes expire in 10 minutes.
                </>
              )}
            </p>

            {error && (
              <div
                className="mb-6 flex items-start gap-2 text-sm p-3 pill-danger"
                data-testid="login-error"
              >
                <ShieldAlert size={16} className="mt-0.5" />{" "}
                <span>{error}</span>
              </div>
            )}

            {sessionNotice && (
              <div
                className="mb-6 border px-3 py-3 text-sm"
                style={{ borderColor: "var(--border)", color: "var(--muted)" }}
                data-testid="session-timeout-notice"
              >
                {sessionNotice}
              </div>
            )}

            {stage === "new-password" ? (
              <form
                onSubmit={submitNewPassword}
                className="space-y-8"
                data-testid="login-new-password-form"
              >
                <div>
                  <label className="field-label">New password</label>
                  <PasswordField
                    value={newPassword}
                    minLength={12}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    testId="login-new-password"
                  />
                </div>
                <div>
                  <label className="field-label">Confirm new password</label>
                  <PasswordField
                    value={confirmPassword}
                    minLength={12}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    testId="login-confirm-password"
                  />
                  <PasswordChecklist
                    password={newPassword}
                    confirm={confirmPassword}
                    personal={[username]}
                    testId="login-password-checklist"
                  />
                </div>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !isPasswordValid(newPassword, {
                      confirm: confirmPassword,
                      personal: [username],
                    })
                  }
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="login-save-new-password"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      Save password <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            ) : stage === "credentials" ? (
              <form
                onSubmit={submitCreds}
                className="space-y-8"
                data-testid="login-credentials-form"
              >
                <div>
                  <label className="field-label">Client username or work email</label>
                  <input
                    className="input-flat"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="j.smith"
                    autoComplete="username"
                    required
                    data-testid="login-username"
                  />
                </div>
                <div>
                  <label className="field-label">Password</label>
                  <PasswordField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    testId="login-password"
                  />
                  <div className="mt-3 text-right">
                    <Link
                      to="/forgot-password"
                      className="text-sm font-semibold underline underline-offset-4"
                      style={{ color: "var(--accent)" }}
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      Continue <ArrowRight size={16} />
                    </>
                  )}
                </button>
                {sso.enabled && (
                  <div data-testid="sso-block">
                    <div
                      className="flex items-center gap-3 my-6"
                      aria-hidden="true"
                    >
                      <span
                        className="h-px flex-1"
                        style={{ background: "var(--border)" }}
                      />
                      <span className="eyebrow text-[9px]">or</span>
                      <span
                        className="h-px flex-1"
                        style={{ background: "var(--border)" }}
                      />
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/start`}
                      target={EMBEDDED ? "_blank" : "_self"}
                      rel={EMBEDDED ? "noopener noreferrer" : undefined}
                      className="btn-ghost w-full flex items-center justify-center gap-2"
                      data-testid="sso-login-button"
                      data-opens={EMBEDDED ? "new-tab" : "same-tab"}
                    >
                      <Building size={16} /> Sign in with Microsoft 365
                    </a>
                    <div className="mt-2 text-center">
                      <Link
                        to="/emergency-access"
                        className="text-[11px] no-underline hover:underline transition-opacity duration-150 opacity-70 hover:opacity-100"
                        style={{ color: "var(--muted)" }}
                        data-testid="emergency-access-link"
                      >
                        Emergency administrator access
                      </Link>
                    </div>
                    <p
                      className="mt-3 text-xs text-center"
                      style={{ color: "var(--muted)" }}
                      data-testid="sso-login-note"
                    >
                      {EMBEDDED
                        ? "For portal administrators. Opens in a new tab — Microsoft does not allow its sign-in page inside an embedded frame."
                        : "For portal administrators."}{" "}
                      Your Microsoft sign-in carries its own verification, so no
                      email code is needed.
                    </p>
                  </div>
                )}
              </form>
            ) : (
              <form
                onSubmit={submitCode}
                className="space-y-8"
                data-testid="login-otp-form"
              >
                <div>
                  <label className="field-label">Verification code</label>
                  <div className="mt-2">
                    <InputOTP
                      maxLength={6}
                      value={code}
                      onChange={setCode}
                      data-testid="login-otp-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="h-14 w-12 text-lg font-mono border-l-0 first:border-l"
                            style={{ borderRadius: 0 }}
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="login-verify-submit"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <>
                        Verify & Sign In <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCode("");
                      setError("");
                    }}
                    disabled={!code.length}
                    className="btn-ghost flex items-center gap-2 disabled:opacity-50"
                    data-testid="login-clear-code"
                  >
                    <Eraser size={14} /> Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStage("credentials");
                      setCode("");
                    }}
                    className="btn-ghost"
                    data-testid="login-back"
                  >
                    Back
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span style={{ color: "var(--muted)" }}>
                    {cooldown > 0
                      ? `You can request another code in ${cooldown}s`
                      : ""}
                  </span>
                  <button
                    type="button"
                    onClick={resendCode}
                    disabled={resending || cooldown > 0}
                    className="btn-ghost flex items-center gap-2 text-sm disabled:opacity-50"
                    data-testid="login-resend-code"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="animate-spin" size={14} /> Sending…
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} /> Didn't get the code? Request a
                        new one
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            <div
              className="mt-12 pt-6 border-t eyebrow"
              style={{ borderColor: "var(--border)", color: "var(--muted)" }}
            >
              Your work email is bound to your account. Contact your program
              administrator if it needs to change.
            </div>
          </motion.div>
        </div>
      </div>
      <EmployerLegalFooter />
    </div>
  );
}
